import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Close as CloseIcon,
  Support as SupportIcon,
  Help as HelpIcon,
  Book as BookIcon,
  VideoLibrary as VideoIcon,
  Chat as ChatIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Settings as SettingsIcon,
  MusicNote as MusicIcon,
  Event as EventIcon,
  Wifi as CloudIcon,
  Security as SecurityIcon,
  Download as DownloadIcon
} from '@mui/icons-material';

interface SupportModalProps {
  open: boolean;
  onClose: () => void;
}

const SupportModal: React.FC<SupportModalProps> = ({ open, onClose }) => {
  const [expandedFaq, setExpandedFaq] = useState<string | false>(false);

  const handleFaqChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  const supportChannels = [
    {
      icon: <ChatIcon sx={{ color: '#10b981' }} />,
      title: 'Live Chat Support',
      description: 'Real-time assistance for technical questions',
      availability: 'Available 24/7 for Pro subscribers',
      action: 'Start Chat',
      premium: true
    },
    {
      icon: <EmailIcon sx={{ color: '#4A90C2' }} />,
      title: 'Email Support',
      description: 'Detailed technical support and troubleshooting',
      availability: 'Response within 24 hours',
      action: 'Send Email',
      premium: false
    },
    {
      icon: <PhoneIcon sx={{ color: '#2E86AB' }} />,
      title: 'Phone Support',
      description: 'Direct phone assistance for urgent issues',
      availability: 'Business hours for Pro subscribers',
      action: 'Call Now',
      premium: true
    },
    {
      icon: <BookIcon sx={{ color: '#5BA3D4' }} />,
      title: 'Documentation',
      description: 'Comprehensive guides and tutorials',
      availability: 'Available 24/7',
      action: 'Browse Docs',
      premium: false
    }
  ];

  const quickStartGuides = [
    {
      icon: <DownloadIcon />,
      title: 'Installation & Setup',
      description: 'Get started with Rafilis Multizone Player',
      time: '10 min read'
    },
    {
      icon: <SettingsIcon />,
      title: 'Zone Configuration',
      description: 'Configure audio zones for your venue',
      time: '15 min read'
    },
    {
      icon: <MusicIcon />,
      title: 'Audio Routing',
      description: 'Set up audio routing and mixing',
      time: '12 min read'
    },
    {
      icon: <EventIcon />,
      title: 'Event Scheduling',
      description: 'Create and manage audio events',
      time: '8 min read'
    },
    {
      icon: <CloudIcon />,
      title: 'Remote Management',
      description: 'Access your system remotely',
      time: '6 min read'
    },
    {
      icon: <SecurityIcon />,
      title: 'Security Settings',
      description: 'Configure security and user access',
      time: '10 min read'
    }
  ];

  const faqData = [
    {
      id: 'installation',
      question: 'How do I install Rafilis Multizone Player?',
      answer: 'Download the installer from your account dashboard, run as administrator, and follow the setup wizard. Ensure your audio interface drivers are installed first.'
    },
    {
      id: 'zones',
      question: 'How many audio zones can I control?',
      answer: 'Rafilis MP supports up to 32 audio zones depending on your hardware configuration. Each zone can be independently controlled for volume, EQ, and content.'
    },
    {
      id: 'formats',
      question: 'What audio formats are supported?',
      answer: 'We support WAV, MP3, FLAC, AAC, and other common formats. For best quality, we recommend using uncompressed WAV files for background music.'
    },
    {
      id: 'hardware',
      question: 'What hardware do I need?',
      answer: 'You need a Windows PC with a professional audio interface (ASIO drivers recommended), sufficient storage for audio files, and network connectivity for remote management.'
    },
    {
      id: 'remote',
      question: 'Can I control the system remotely?',
      answer: 'Yes! Pro subscribers can access the web dashboard from anywhere to monitor and control their audio system, schedule events, and receive notifications.'
    },
    {
      id: 'backup',
      question: 'How does backup and recovery work?',
      answer: 'Your configurations are automatically backed up to the cloud. Audio files can be synchronized across multiple locations, and system settings can be restored quickly.'
    },
    {
      id: 'integration',
      question: 'Does it integrate with hotel management systems?',
      answer: 'Yes, we offer integrations with major PMS systems for hotels. This allows automatic audio adjustments based on room occupancy and events.'
    },
    {
      id: 'licensing',
      question: 'How does licensing work for multiple venues?',
      answer: 'We offer volume licensing for multiple locations. Each venue requires a separate license, but management can be centralized through our enterprise dashboard.'
    }
  ];

  const troubleshootingSteps = [
    'Check that your audio interface drivers are up to date',
    'Verify network connectivity for cloud features',
    'Ensure Windows audio service is running',
    'Check firewall settings for Rafilis MP',
    'Restart the Rafilis MP service',
    'Contact support if issues persist'
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: '#ffffff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SupportIcon sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Rafilis Support Center
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Get help with Multizone Player
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#ffffff',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Support Channels */}
        <Box sx={{ p: 4, background: '#ffffff' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1e293b' }}>
            Get Support
          </Typography>
          <Grid container spacing={3}>
            {supportChannels.map((channel, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{
                  height: '100%',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)',
                  border: '1px solid rgba(74, 144, 194, 0.1)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(74, 144, 194, 0.15)',
                    border: '1px solid rgba(74, 144, 194, 0.3)'
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {channel.icon}
                      <Typography variant="h6" sx={{ ml: 1.5, fontWeight: 600, color: '#1e293b' }}>
                        {channel.title}
                      </Typography>
                      {channel.premium && (
                        <Chip
                          label="Pro"
                          size="small"
                          sx={{
                            ml: 'auto',
                            background: '#10b981',
                            color: '#ffffff',
                            fontWeight: 600
                          }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 2, lineHeight: 1.6 }}>
                      {channel.description}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9ca3af', mb: 2, display: 'block' }}>
                      {channel.availability}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: '#4A90C2',
                        color: '#4A90C2',
                        '&:hover': {
                          background: '#4A90C2',
                          color: '#ffffff'
                        }
                      }}
                    >
                      {channel.action}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider />

        {/* Quick Start Guides */}
        <Box sx={{ p: 4, background: '#f8fafc' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1e293b' }}>
            Quick Start Guides
          </Typography>
          <Grid container spacing={2}>
            {quickStartGuides.map((guide, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{
                  background: '#ffffff',
                  border: '1px solid rgba(74, 144, 194, 0.1)',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    border: '1px solid rgba(74, 144, 194, 0.3)',
                    transform: 'translateY(-1px)'
                  }
                }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Box sx={{ color: '#4A90C2', mr: 1.5 }}>
                        {guide.icon}
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {guide.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 1, lineHeight: 1.4 }}>
                      {guide.description}
                    </Typography>
                    <Chip
                      label={guide.time}
                      size="small"
                      sx={{
                        background: 'rgba(74, 144, 194, 0.1)',
                        color: '#4A90C2',
                        fontSize: '0.7rem'
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider />

        {/* FAQ Section */}
        <Box sx={{ p: 4, background: '#ffffff' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1e293b' }}>
            Frequently Asked Questions
          </Typography>
          {faqData.map((faq) => (
            <Accordion
              key={faq.id}
              expanded={expandedFaq === faq.id}
              onChange={handleFaqChange(faq.id)}
              sx={{
                mb: 1,
                background: '#f8fafc',
                border: '1px solid rgba(74, 144, 194, 0.1)',
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  border: '1px solid rgba(74, 144, 194, 0.3)'
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    margin: '12px 0'
                  }
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6 }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        <Divider />

        {/* Troubleshooting */}
        <Box sx={{ p: 4, background: '#f8fafc' }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1e293b' }}>
                Basic Troubleshooting
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                Try these steps before contacting support:
              </Alert>
              <List sx={{ p: 0 }}>
                {troubleshootingSteps.map((step, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Typography variant="body2" sx={{
                        background: '#4A90C2',
                        color: '#ffffff',
                        borderRadius: '50%',
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.8rem',
                        fontWeight: 600
                      }}>
                        {index + 1}
                      </Typography>
                    </ListItemIcon>
                    <ListItemText
                      primary={step}
                      primaryTypographyProps={{
                        sx: { fontSize: '0.9rem', color: '#475569' }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1e293b' }}>
                System Requirements
              </Typography>
              <Box sx={{
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                borderRadius: '12px',
                p: 3,
                color: '#ffffff'
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Minimum Requirements
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.6, mb: 2 }}>
                  • Windows 10/11 (64-bit)<br />
                  • 8GB RAM minimum (16GB recommended)<br />
                  • Professional audio interface with ASIO drivers<br />
                  • 500MB storage space<br />
                  • Network connectivity for cloud features<br />
                  • DirectX 11 compatible graphics
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Recommended Audio Interfaces
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                  • RME series<br />
                  • Focusrite Scarlett/Clarett<br />
                  • PreSonus Studio series<br />
                  • MOTU interfaces
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Support Status */}
        <Box sx={{
          p: 4,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: '#ffffff',
          textAlign: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Support Status
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
            <Chip
              icon={<CheckIcon />}
              label="All Systems Operational"
              sx={{
                background: '#10b981',
                color: '#ffffff',
                fontWeight: 600
              }}
            />
            <Chip
              label="Response Time: < 2 hours"
              sx={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                fontWeight: 500
              }}
            />
            <Chip
              label="99.9% Uptime"
              sx={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                fontWeight: 500
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Need immediate assistance? Pro subscribers can access live chat support 24/7.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SupportModal;